import { Network } from "@keyban/types";
import type { Chain, PublicClient, Transport } from "viem";
import {
  createPublicClient,
  createWalletClient,
  hashMessage,
  hashTypedData,
  http,
  keccak256,
  parseSignature,
  serializeTransaction,
} from "viem";
import { publicKeyToAddress, toAccount } from "viem/accounts";
import * as chains from "viem/chains";

import { KeybanClientBase, KeybanClientConfig, MetadataConfig } from "~/client";
import { KeybanEvmAccount } from "~/evm/account";

/**
 * EVM network client that provisions an {@link KeybanEvmAccount} using the Keyban ECDSA service.
 * Handles client share fetching/persistence, public key derivation, and wiring of signers for viem.
 * @example
 * import { KeybanClient, Network } from "@keyban/sdk-base";
 *
 * const client = new KeybanClient({
 *   apiUrl: "https://api.prod.keyban.io",
 *   appId: "YOUR_APP_ID",
 *   network: Network.EthereumAnvil,
 * });
 * const account = await client.initialize();
 * console.log(account.address);
 */
export class KeybanEvmClient extends KeybanClientBase {
  #viem: {
    chain: Chain;
    transport: Promise<Transport>;
    publicClient: Promise<PublicClient<Transport, Chain>>;
  };

  constructor(
    config: KeybanClientConfig,
    metadataConfig?: Promise<MetadataConfig>,
  ) {
    super(config, metadataConfig);

    const transport = this.metadataConfig.then((config) =>
      http(config.network.rpcUrl),
    );

    const chain = {
      [Network.EthereumAnvil]: chains.anvil,
      [Network.PolygonAmoy]: chains.polygonAmoy,
      [Network.StarknetDevnet]: null,
      [Network.StarknetSepolia]: null,
      [Network.StarknetMainnet]: null,
      [Network.StellarQuickstart]: null,
      [Network.StellarTestnet]: null,
      [Network.StellarMainnet]: null,
    }[this.network]!;

    const publicClient = transport.then((transport) =>
      createPublicClient({ chain, transport }),
    );

    this.#viem = { chain, transport, publicClient };
  }

  /**
   * Initialize the EVM account for the configured network.
   * Retrieves or creates a client share (via DKG), derives the public key/address,
   * and returns a {@link KeybanEvmAccount} bound to viem clients.
   * @returns A ready-to-use {@link KeybanEvmAccount}.
   * @example
   * const account = await evmClient.initialize();
   * console.log(account.publicKey, account.address);
   */
  async initialize(): Promise<KeybanEvmAccount> {
    const key = `ecdsa:${this.network}`;

    let clientShare = await this.clientShareProvider.get(key);

    if (!clientShare) {
      clientShare = await this.api.ecdsa.dkg();
      await this.clientShareProvider.set(key, clientShare);
    }

    const publicKey = await this.api.ecdsa.publicKey(clientShare);

    const account = toAccount({
      address: publicKeyToAddress(publicKey),
      signMessage: async ({ message }) => {
        const hash = hashMessage(message, "hex");
        return this.api.ecdsa.sign(clientShare, hash);
      },
      signTransaction: async (transaction, options) => {
        const serializer = options?.serializer ?? serializeTransaction;

        // For EIP-4844 Transactions, we want to sign the transaction payload body (tx_payload_body) without the sidecars (i.e., without the network wrapper).
        // See: https://github.com/ethereum/EIPs/blob/e00f4daa66bd56e2dbd5f1d36d09fd613811a48b/EIPS/eip-4844.md#networking
        const signableTransaction =
          transaction.type === "eip4844"
            ? { ...transaction, sidecars: false }
            : transaction;

        const signature = await this.api.ecdsa
          .sign(clientShare, keccak256(serializer(signableTransaction)))
          .then(parseSignature);

        return serializer(transaction, signature);
      },
      signTypedData: async (typedDataDefinition) => {
        const hash = hashTypedData(typedDataDefinition);
        const data = await this.api.ecdsa.sign(clientShare, hash);
        return data;
      },
    });

    // Assign the public key to the account
    account.publicKey = publicKey;

    const publicClient = await this.#viem.publicClient;
    const walletClient = createWalletClient({
      chain: publicClient.chain,
      transport: await this.#viem.transport,
      account,
    });

    return new KeybanEvmAccount(this.api, publicClient, walletClient);
  }
}
